import { MemoryMessageToSend } from "@/components/chat/chat-conversation";
import { ChatLayout } from "@/components/chat/chat-layout";
import { Button } from "@/components/ui/button";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useChatColumns } from "@/hooks/useChatColumns";
import { useLlmSelection } from "@/hooks/useLlmSelection";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { setWindowTitle } from "@/lib/window-title";
import { ConversationDTO } from "@shaarp/common";
import { SidebarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function ChatConversationPage({
  conversation,
  onCreated,
  onSidebarToggle,
}: {
  conversation: ConversationDTO | undefined | null;
  onCreated: (conversation: ConversationDTO) => void;
  onSidebarToggle: () => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toastError = useToastError();
  const [creatingMessage, setCreatingMessage] =
    useState<MemoryMessageToSend | null>(null);
  const { llmSelection } = useLlmSelection();

  // The company side panel opens non-modally from chat mentions; dock the
  // conversation to its left so it sits beside the panel rather than behind it
  // — unless it is laid over the chat's own side panel, on a screen with room
  // for two columns only.
  const { isCompanyPanelDocked: isSidePanelDocked } = useChatColumns();

  useEffect(() => {
    setWindowTitle(
      conversation && conversation.title
        ? t("chat.conversation.windowTitle", { title: conversation.title })
        : t("chat.conversation.windowTitleDefault")
    );
  }, [conversation, t]);

  const createNewConversation = async (message?: MemoryMessageToSend) => {
    // Show the message optimistically while the conversation is created.
    if (message) {
      setCreatingMessage(message);
    }
    try {
      const { data } = await api.post<ConversationDTO>("/conversation", {
        llmId: message?.llmId || llmSelection,
        message: message?.text || null,
      });
      // Pass the pending message in navigation state for ChatConversation to send
      navigate(`/chat/${data.id}`, {
        replace: true,
        state: message ? { pendingMessage: message } : undefined,
      });
      onCreated(data);
    } catch {
      setCreatingMessage(null);
      toastError();
    }
  };

  return (
    <div
      className={cn(
        "flex absolute top-0 left-0 flex-col flex-1 w-full h-full",
        // Track the side panel's slide exactly so the chat's right edge never
        // outruns the panel's left edge and exposes the page background behind
        // it: same easing, and the panel's own open/close durations
        // (400ms / 250ms). Width matches its sm:max-w-(--company-panel-width).
        "transition-[padding] ease-in-out",
        isSidePanelDocked
          ? "duration-[400ms] sm:pr-(--company-panel-width)"
          : "duration-[250ms]",
      )}
    >
      <Button
        size="icon"
        variant="outline"
        onClick={onSidebarToggle}
        className="absolute top-2 left-3 z-10"
      >
        <SidebarIcon className="w-4 h-4" />
      </Button>

      <div className="flex relative flex-1 w-full">
        <ChatLayout
          conversation={conversation}
          pendingMessage={creatingMessage}
          isCreatingConversation={!!creatingMessage}
          triggerCreateNewConversation={createNewConversation}
        />
      </div>
    </div>
  );
}
